/**
 * Additional registrations on an extension (owner, 18 September 2026): up to two more SIP logins,
 * each named by whoever adds it -- luke-meridian, sarah-meridian -- each with its own password and
 * its own switch for whether inbound calls ring it. Unique on the account: adding a name that is
 * already there is refused, never merged.
 *
 * The username is the name plus the account (the first label of its SIP domain), exactly as the
 * main registration is the number plus the account (1001-meridian, docs/26 E8).
 */
import { z } from 'zod';
import { IdSchema } from './common.js';

export const ADDITIONAL_REGISTRATIONS_MAX = 2;

/**
 * Lower-case letters, digits, dot and underscore, starting with a letter; 2 to 30 characters.
 * No hyphen: it separates the name from the account, and the edge reads it that way. Starting with
 * a letter keeps a name from ever looking like an extension's own login (1001-meridian).
 */
export const REGISTRATION_NAME_RE = /^[a-z][a-z0-9._]{1,29}$/;

export const RegistrationCreateSchema = z.object({
  name: z.string().trim().toLowerCase().regex(REGISTRATION_NAME_RE,
    'Use 2 to 30 letters, digits, dots or underscores, starting with a letter'),
}).strict();

export const RegistrationUpdateSchema = z.object({ ring_enabled: z.boolean() }).strict();

export const AdditionalRegistrationSchema = z.object({
  id: IdSchema,
  name: z.string(),
  username: z.string(),
  ring_enabled: z.boolean(),
  created_at: z.string(),
});
export type AdditionalRegistration = z.infer<typeof AdditionalRegistrationSchema>;

export interface ExtensionRegistrations {
  /** The extension's own login, e.g. 1001-meridian. */
  main: string | null;
  /** The account part every name gets, e.g. "meridian". */
  account: string;
  additional: AdditionalRegistration[];
  max: number;
}

/** The username a name becomes on an account. */
export const registrationUsername = (name: string, account: string): string => `${name}-${account}`;

/**
 * The workspace's own phone (docs/36 §8.1, migration 0104).
 *
 * A name the edge already accepts: letters then digits, no hyphen, so `web1001-meridian` splits
 * into the name and the account exactly as every other login does (arc_sip_named_usernames_only).
 * Derived rather than chosen, because one seat has one workspace and nobody should have to think
 * of a name to make a call in their browser.
 */
export const browserPhoneName = (extensionNumber: string): string => `web${extensionNumber}`;

export interface BrowserPhone {
  /** Whether this SEAT may take calls in the browser: the `softphone` feature (0105). */
  entitled: boolean;
  /** Whether it can, here and now: entitled, and a platform with an edge that takes browser calls. */
  available: boolean;
  ws_url: string | null;
  /** The account's own SIP domain: the realm the password is hashed with. */
  realm: string;
  /**
   * The domain the browser registers in and addresses colleagues in: the one SIP name every
   * client uses (ARC_SIP_REGISTRAR_HOST, 6 October 2026). The edge maps it to `realm` from the
   * username; the browser hashes in whatever realm it is challenged in.
   */
  domain: string;
  username: string;
  display_name: string;
  /** Whether ordinary calls to the seat ring the browser too. */
  ring_enabled: boolean;
  signed_in: boolean;
}
